import { apiRequest, ErrorType } from "../api";

export interface FirstStepData {
    name: string;
    email: string;
    password: string;
    phone: string;
}

export interface FirstStepResponse {
    userId: string;
    // ... other response fields
}

export interface SecondStepData {
    cpfCnpj: string;
    subdomain: string;
}

export interface PaymentCreditCard {
    number: string;
    holder: string;
    expirationDate: string;
    cvv: string;
}

export interface Address {
    street: string;
    number: string;
    neighborhood: string;
    zipcode: string;
    city: string;
    state: string;
    complement: string;
}

export interface ThirdStepData {
    paymentCreditCard?: PaymentCreditCard;
    address: Address;
    ip: string;
}

export interface LoginData {
    email: string;
    password: string;
    subdomain: string;
}

export interface ForgotPasswordResponse {
    msg: string;
}

interface UserLogin {
    accessToken?: string;
    user?: UserClass;
}

interface UserClass {
    _id?: string;
    email?: string;
    phone?: string;
    name?: string;
    status?: string;
    permissions?: string[];
    createdAt?: Date;
    updatedAt?: Date;
    __v?: number;
    organizationId?: string;
}

// Auth services - Using the [data, error] pattern
export const authService = {
    async firstStep(data: FirstStepData): Promise<[FirstStepResponse | null, ErrorType | null]> {
        return apiRequest<FirstStepResponse>({
            loaderKey: "createAccount",
            method: 'post',
            url: "/app/users/first-step",
            data,
            ignoreError: true
        });
    },

    async secondStep(userId: string, data: SecondStepData): Promise<[any | null, ErrorType | null]> {
        return apiRequest<any>({
            loaderKey: "createAccount",
            method: 'post',
            url: `/app/users/second-step/${userId}`,
            data
        })
    },

    async thirdStep(userId: string, data: ThirdStepData): Promise<[any | null, ErrorType | null]> {
        return apiRequest<any>({
            loaderKey: "createAccount",
            method: 'post',
            url: `/app/users/third-step/${userId}`,
            data
        });
    },

    async login(
        data: LoginData,
        options?: { ignoreError?: boolean }
    ): Promise<[UserLogin | null, ErrorType | null]> {
        const [response, error] = await apiRequest<UserLogin>({
            loaderKey: "login",
            method: 'post',
            url: "/app/auth/login",
            data,
            ignoreError: options?.ignoreError,
        });
        // Save token to localStorage if successful
        if (response?.accessToken) {
            localStorage.setItem("auth_token", response.accessToken);
            localStorage.setItem("user", JSON.stringify(response.user));
        }

        return [response, error];
    },

    /** Envia o e-mail com o código de recuperação (o backend sempre responde com uma msg genérica, mesmo se o e-mail não existir). */
    async requestPasswordReset(email: string): Promise<[ForgotPasswordResponse | null, ErrorType | null]> {
        return apiRequest<ForgotPasswordResponse>({
            loaderKey: "forgotPassword",
            method: 'post',
            url: "/app/auth/forgot-password",
            data: { email },
        });
    },

    /** Confirma o código recebido por e-mail e define a nova senha — mesmo endpoint do passo anterior, com code+newPassword no body. */
    async resetPassword(
        email: string,
        code: string,
        newPassword: string
    ): Promise<[ForgotPasswordResponse | null, ErrorType | null]> {
        return apiRequest<ForgotPasswordResponse>({
            loaderKey: "resetPassword",
            method: 'post',
            url: "/app/auth/forgot-password",
            data: { email, code, newPassword },
        });
    },

    logout() {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user");
        window.location.href = '/login';
    },

    // Guarda `response.user` (não o wrapper `{accessToken, user}`) no localStorage — o retorno
    // aqui precisa refletir isso, senão todo `.getCurrentUser()?._id/.name` no app conta com um
    // aninhamento que nunca existiu de verdade.
    getCurrentUser(): UserClass | null {
        const userStr = localStorage.getItem("user");
        return userStr ? JSON.parse(userStr) : null;
    },

    isAuthenticated() {
        return !!localStorage.getItem("auth_token");
    },
};
